/* "They are already set up": the manual override for somebody made by hand.
 *
 * Sometimes a person is added to the systems directly (an account, a licence, a
 * SignIn entry, an EZ Leave record) without going through Add a joiner. They are
 * then stuck in the To set up queue for ever, and the only ways out both do the
 * work a second time: Finish creates an EZ Leave record, and the wizard opens an
 * HR and Accounts case whose completion provisions everything again.
 *
 * This is the third way. It stamps them as set up and creates NOTHING, and says
 * so before it is pressed, because "skip the onboarding" is only safe when the
 * person pressing it knows precisely what is being skipped. The rules live in
 * lib/lifecycle.ts (canMarkSetUp, markSetUpPatch) where they are tested.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canMarkSetUp, markSetUpPatch } from '../../lib/lifecycle';
import type { IPerson } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import { Button, useToast } from '../ui';
import s from './screens.module.scss';

export function AlreadySetUp({
  person,
  onDone,
}: {
  person: IPerson;
  /** Called after the stamp is written, so the caller can close or navigate. */
  onDone: () => void;
}): JSX.Element {
  const svc = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');

  /* Only asked when the Onboarding app is configured: without it there are no
     cases to collide with. */
  const cases = useQuery({
    queryKey: qk.joinerCases,
    queryFn: () => svc.joiners!.cases(),
    enabled: !!svc.joiners,
  });
  const openCase = (cases.data ?? []).some(
    (c) => c.stage !== 'Complete' && c.upn.toLowerCase() === (person.upn || '').toLowerCase(),
  );
  const verdict = canMarkSetUp(person, openCase);
  /* Not decided until the case list has arrived: a button that is enabled for a
     moment and then refuses is worse than one that waits. */
  const waiting = !!svc.joiners && cases.isLoading;

  const mark = useMutation({
    meta: { quiet: true },
    mutationFn: () => {
      const today = new Date().toISOString().slice(0, 10);
      return svc.people.update(person.id, markSetUpPatch(person, today, reason));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.people });
      toast(`${person.name} is marked as set up. Nothing was created.`, 'ok');
      onDone();
    },
  });

  return (
    <div className={s.stepRow}>
      <b>Already set up by hand?</b>
      <div className={s.stepDetail}>
        For somebody whose accounts were made outside the onboarding process. This only takes them
        out of the queue.
      </div>

      {!verdict.ok ? (
        <div className={s.stepDetail}>{verdict.why}</div>
      ) : !asking ? (
        <Button disabled={waiting} onClick={() => setAsking(true)}>
          Mark as already set up…
        </Button>
      ) : (
        <>
          <div className={s.ownedNote}>
            It creates nothing: no hub access, folder, welcome email, EZ Leave record, SignIn entry or
            Training Matrix row, and it asks nothing of HR or Accounts. If they are not in those
            systems yet, use &ldquo;Create them in SignIn, the Training Matrix and EZ Leave&rdquo;
            below. A missing start date or depot stays listed under Incomplete records.
          </div>
          <input
            className={s.stepSelect}
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional note, e.g. started 4 Nov, set up by hand"
            aria-label="Why they are being marked as set up"
            maxLength={200}
          />
          <div>
            <Button primary disabled={mark.isPending} onClick={() => mark.mutate()}>
              {mark.isPending ? 'Saving…' : `Yes, ${person.name} is already set up`}
            </Button>{' '}
            <Button disabled={mark.isPending} onClick={() => setAsking(false)}>Cancel</Button>
          </div>
          {mark.error ? <div className={s.stepError}>{String((mark.error as Error).message)}</div> : null}
        </>
      )}
    </div>
  );
}
